import React, { useEffect } from 'react'
import { Flex } from '@mprolab/uikit'
import { StyledBiennale } from 'components'
import MainWrapper from 'layouts/MainWrapper'
import styled, { useTheme } from 'styled-components'
import Image from 'components/CompatImage'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useLifetimeTokens } from 'state/global/hooks'
import { useGetLicensesQuery } from 'state/services/indexNode/indexNode'
import { isEmpty } from 'lodash'
import UseAnimations from 'react-useanimations'
import loading2 from 'react-useanimations/lib/loading2'
import { LicenseStatus, SessionType } from '@mprolab/sdk'
import PromoBanners, { PromoBannersType } from 'components/PromoBanners/PromoBanners'
import dottedBackground from '../../public/images/dottedBackgroundPattern1.webp'
import { RewardToClaim, CurrentCycleData, CycleLogs, EmptyRewards } from './components'
import { observer } from 'mobx-react-lite'
import { indexNodeStore } from 'mobxStores/IndexNodeStore'
import { setLifetimeTokens } from 'state/global/actions'
import { useAwaitingWalletVerificationModal } from 'components/widgets/VerificationModals'
import { useVerifyIndexNode } from 'hooks/useVerifyIndexNode'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'state'
import isNil from 'lodash/isNil'
import { useIsUserVerified } from 'state/user/hooks'
import useToast from 'hooks/useToast'
import AppSessionRequiredPanel from 'components/AppSessionRequiredPanel'
import { useNavigate } from 'react-router-dom'

const StyledTitle = styled(StyledBiennale)`
  font-size: 32px;
  margin-bottom: 34px;

  ${({ theme }) => theme.mediaQueries.tablet} {
    font-size: 24px;
  }
`

const DottedBackgroundWrapper = styled(Flex)`
  position: absolute;
  top: -150px;
  left: 70px;
  width: 100%;
  height: 700px;
  z-index: -1;

  ${({ theme }) => theme.mediaQueries.tablet} {
    height: 550px;
    left: 0;
    left: 80px;
  }
`

const Rewards: React.FC = observer(() => {
  const { t } = useTranslation()
  const { toastError } = useToast()
  const { account, provider } = useWeb3React()
  const { colors } = useTheme()
  const isAppVerified = useIsUserVerified()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = lifetimeTokens?.[account]
  const {
    data: licenses,
    isLoading: licensesLoading,
    error
  } = useGetLicensesQuery({ account, accessToken }, { skip: isNil(accessToken) || !isAppVerified })
  const noActiveLicenses = licenses?.every((license) => license.status === LicenseStatus.TERMINATED)
  const { onPresentAwaitingVerificationModal, onModalClose } = useAwaitingWalletVerificationModal({})
  const verifyIndexNode = useVerifyIndexNode()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { isLoading } = indexNodeStore

  const rewardsContent = () => {
    if (account && !isAppVerified) {
      return <AppSessionRequiredPanel />
    }
    switch (true) {
      case licensesLoading || isLoading:
        return (
          <Flex fullWidth justifyContent="center" alignItems="center" minHeight={300}>
            <UseAnimations animation={loading2} strokeColor={colors.gray6} size={64} />
          </Flex>
        )
      case isEmpty(licenses):
        return (
          <EmptyRewards
            title={t('rewards.noLicenses')}
            description={t('rewards.goToNodesTab')}
            buttonText={t('rewards.getALicense')}
            redirectTo="/user-licenses"
          />
        )
      case noActiveLicenses:
        return (
          <EmptyRewards
            title={t('rewards.noActiveLicenses')}
            description={t('rewards.noActiveLicensesDescription')}
            buttonText={t('rewards.goToLicensesPanel')}
            redirectTo="/user-licenses"
          />
        )
      default:
        return (
          <>
            <Grid container columnSpacing={3} rowSpacing={3}>
              <Grid item xs={12} md={6} order={{ xs: 3, md: 1 }}>
                <CurrentCycleData />
              </Grid>
              {/* <Grid item xs={12} md={2.5} order={{ xs: 1, md: 2 }}>
                <UntilNewCycleCounter />
              </Grid> */}
              <Grid item xs={12} md={6} order={{ xs: 2, md: 3 }}>
                <RewardToClaim />
              </Grid>
              {/*
                The per-licence breakdown is deliberately NOT here. It used to
                be planned for this spot, but its content moved into the
                activity log behind "Claim history", where the same numbers sit
                next to what was minted, claimed and charged — operator
                decision, 2026-08-30. This comment used to promise a widget
                that was never mounted, which read as a missing feature.
              */}
              <Grid item xs={12} order={{ xs: 5 }}>
                <CycleLogs />
              </Grid>
            </Grid>
          </>
        )
    }
  }

  const verifyAndSetToken = async () => {
    /*
     * Nothing is attempted until the wallet can actually sign.
     *
     * `account` and `provider` do not arrive together: the address is
     * synchronous, the provider is set by an async effect awaiting
     * `connector.getProvider()`. Without this guard the effect below fired the
     * moment the address appeared, `verifyIndexNode` returned "Wallet account
     * or provider missing" immediately, and the user got an error toast on
     * first entry that went away on refresh — because by then the provider was
     * there. NodesStorage already had this guard; these two pages did not.
     */
    if (!account || !provider) return
    indexNodeStore.setIsLoading(true)
    onPresentAwaitingVerificationModal()
    await verifyIndexNode({
      account,
      provider,
      successCallback: async (accessToken) => {
        dispatch(setLifetimeTokens({ account, accessToken }))
        onModalClose()
        indexNodeStore.setIsLoading(false)
      },
      errorCallback: async () => {
        navigate('/')
        onModalClose()
        indexNodeStore.setIsLoading(false)
      },
      sessionType: SessionType.LIFETIME
    })
  }

  useEffect(() => {
    if (!account || !provider || !isAppVerified) return
    // @ts-ignore
    if (isNil(accessToken) || error?.data?.message === 'Invalid session: Session not found') {
      void verifyAndSetToken()
    }
  }, [account, provider, isAppVerified, accessToken, error])

  return (
    <>
      <Flex pt="132px" justifyContent="center">
        <PromoBanners type={PromoBannersType.REWARD} />
      </Flex>
      <MainWrapper>
        <Flex fullWidth mt="32px" mb="234px" flexDirection="column">
          <StyledTitle>{t('common.rewards')}</StyledTitle>
          {rewardsContent()}
        </Flex>
        <DottedBackgroundWrapper>
          <Image src={dottedBackground} objectFit="cover" loading="eager" />
        </DottedBackgroundWrapper>
      </MainWrapper>
    </>
  )
})

export default Rewards
